import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { type JSX, useMemo } from 'react';
import { Link } from 'react-router';

import {
  StyledButton,
  StyledHiddenLabelSpan,
  StyledIconButton,
  StyledProgressLayerContainerDiv,
} from './Button.styled';

export interface ButtonProps extends Omit<MuiButtonProps, 'color' | 'href'> {
  color?: MuiButtonProps['color'] | 'default';
  icon?: boolean;
  loading?: boolean;
  allowed?: boolean;
  notAllowedMessage?: string;
  href: string;
  'data-test'?: string | ((row) => string);
}

export function ButtonLink({
  variant = 'contained',
  size = 'medium',
  color = variant === 'outlined' ? 'default' : 'secondary',
  loading = false,
  disabled = false,
  allowed = true,
  icon = false,
  'data-test': dataTest = 'button',
  notAllowedMessage = 'You need permission to perform this action',
  children,
  onClick,
  href,
  ...restProps
}: ButtonProps): JSX.Element {
  const circularProgress = useMemo(() => {
    const CircularProgressColor: Record<NonNullable<ButtonProps['variant']>, string> = {
      contained: 'inherit',
      outlined: color,
      text: 'inherit',
    };

    const CircularProgressColorSize: Record<NonNullable<MuiButtonProps['size']>, number> = {
      large: 18,
      medium: 16,
      small: 14,
    };

    return (
      <StyledProgressLayerContainerDiv>
        <CircularProgress
          color={CircularProgressColor[variant] as MuiButtonProps['color']}
          size={CircularProgressColorSize[size]}
        />
      </StyledProgressLayerContainerDiv>
    );
  }, [color, variant, size]);

  const label = useMemo(() => {
    if (loading) {
      return <StyledHiddenLabelSpan>{children}</StyledHiddenLabelSpan>;
    }
    return children;
  }, [loading, children]);

  const ButtonComponent = (icon ? StyledIconButton : StyledButton) as React.ElementType;

  if (!allowed) {
    return (
      <Tooltip title={notAllowedMessage}>
        <span>
          <ButtonComponent
            {...restProps}
            component={Link}
            data-test={dataTest}
            disabled
            nativeButton={false}
            size={size}
            variant={variant}
          >
            {children}
          </ButtonComponent>
        </span>
      </Tooltip>
    );
  }

  return (
    <ButtonComponent
      component={Link}
      data-test={dataTest}
      disableRipple={variant === 'text'}
      nativeButton={false}
      color={color}
      disabled={disabled}
      href={href}
      loading={loading}
      size={size}
      to={href}
      variant={variant}
      {...restProps}
    >
      {label}
      {loading && circularProgress}
    </ButtonComponent>
  );
}
